import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, selectLanguage } from '@moderepo/mode-ui-state';
import { BaseInfoCompProps, capitalizeAll, MultiSeriesDataPoint, Time, TimeSeriesChartPanel, Language, getLanguageByCode, TimeUnit, ChartSettings,
    BaseCompSubAction, BaseCompAction, GenericMessageBox } from '@moderepo/mode-app-components';
import { Agent, NumericMetricInfo } from '@moderepo/mode-internal-apis';
import moment from 'moment';
import { TFunction } from 'i18next';
import { MetricMetadata, HeatMapDashboardWidgetSettings, WidgetDataViewLayout } from './dashboardTypes';
import { useRitzTranslation } from '../../utils/customHooks';
import { TSDashboardWidgetRequiredViewData } from './dashboardLogic';
import { WaffleChart, WaffleChartProps, WaffleChartDataPoint } from '../time-series/WaffleChart';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../../utils/constants';
import { createChartExportSettings, getPreferredLanguage } from '../../utils/utils';
import { useWidgetDataSource } from './dashboardUtils';



const useStyle = makeStyles((theme: Theme) => {
    return {
        widget: {
            width    : '100%',
            height   : '100%',
            minHeight: 'auto',
        },

        widgetViewContent: {
            width  : '100%',
            height : '100%',
            padding: theme.spacing(2),
        },

        chartsContainer: {
            display                                      : 'flex',
            width                                        : '100%',
            height                                       : '100%',
            alignItems                                   : 'center',
            justifyContent                               : 'center',
            [`&.layout-${WidgetDataViewLayout.VERTICAL}`]: {
                flexDirection: 'column',
            },
            [`&.layout-${WidgetDataViewLayout.HORIZONTAL}`]: {
                flexDirection: 'row',
            },
        },

        noDataMessage: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            flex          : 1,
        },
    };
}, { name: 'HeatMapDashboardWidgetDataViewContainer', index: 2 });



/**
 * WaffleChartDataPoint is just a base interface for the Data objects. If does not have all the props we need so we need to extends
 * it and add additional props that HeatMap data require
 */
interface HeatMapDataPoint extends WaffleChartDataPoint {
    // Each heat map data point needs to tell the chart which cell (row and col) it belongs to
    readonly row: string;
    readonly col: string;

    readonly value: number | null;

    // The color of the value based on which threshold it reached.
    readonly color: string;
}


/**
 * WaffleChart doesn't know about the structure of the HeatMapDataPoint. We need to tell it which field to use for row,
 * which field to use for col data, value, color, etc...
 */
const heatMapDataFieldSettingsForToWaffleChart: WaffleChartProps['dataFieldSettings'] = {
    rowField  : 'row',
    colField  : 'col',
    valueField: 'value',
    colorField: 'color',
};



/**
 * This is the interface that contains all the HeatMap information for 1 metric.
 */
interface HeatMapMetricData {
    readonly metricInfo: NumericMetricInfo;
    readonly dataPoints: readonly HeatMapDataPoint[];
    readonly rowLabels: readonly string[];
    readonly colLabels: readonly string[];
}


/**
 * The set of colors to use for painting the data point values on the heat map
 */
enum HeatMapColor {
    CRITICAL_LOW = '#1B91AD',   // Use cyan/blue color to represent TOO LOW value to indicate the value is getting cold
    WARNING_LOW = '#23BBD0',
    NORMAL = '#32E6D0',
    WARNING_HIGH = '#F2FD70',   // Use orange/red color to represent TOO HIGH value to indicate the value is getting hot
    CRITICAL_HIGH = '#FD5394',
    NO_DATA = '#eeeeee',
}



/**
 * The Date format we will use for format the data point's timestamp
 */
const fullDateFormat: Intl.DateTimeFormatOptions = {
    year  : 'numeric',
    month : 'short',
    day   : '2-digit',
    hour  : '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
};


/**
 * The maximum # of rows we allow. If there are more rows than this MAX_ROWS, we will start removing rows that don't have data. However, if there
 * are more than MAX_ROWS that have data then we will show them all, we will not remove rows that have data just because we have too many rows.
 * For example, if there are 100 rows but only 70 rows have data and MAX_ROWS is 50, we will remove only the 30 rows that don't have data and show
 * 70 rows even though MAX_ROWS is 50.
 *
 * We will pick a high number because when there are this many rows, it usually means there is something wrong with the data. The data interval
 * is probably vry big and there are a lot of gap in between.
 */
const MAX_ROWS = 5000;



/**
 * Helper function to check if the value reached the threshold and return a color for the value based on which threshold the value reached.
 * @param metadataSetting
 * @param value
 */
const getValueColorBasedOnThreshold = (metricMetadata: MetricMetadata | undefined, value: number | null | undefined): HeatMapColor => {
    if (metricMetadata && value !== null && value !== undefined) {

        // NOTE: Not all thresholds are set so we only need to compare to the thresholds that have some defined value

        // IMPORTANT NOTE: We need to check if the value passed the CRITICAL threshold level BEFORE checking if it passed below WARNING
        // level because if a value passed the CRITICAL threshold, it should also passed the WARNING threshold. If we check the
        // warning threshold first, we will treat it as passing WARNING threshold and only show WARNING alert when we should have
        // shown CRITICAL alert
        if (metricMetadata.lowValueCriticalThreshold !== undefined && value <= metricMetadata.lowValueCriticalThreshold) {
            return HeatMapColor.CRITICAL_LOW;
        }
        if (metricMetadata.lowValueWarningThreshold !== undefined && value <= metricMetadata.lowValueWarningThreshold) {
            return HeatMapColor.WARNING_LOW;
        }

        // Similar to the check for LOW threshold. We need to check for HIGH CRITICAL threshold before checking for HIGH WARNING threshold
        if (metricMetadata.highValueCriticalThreshold !== undefined && value >= metricMetadata.highValueCriticalThreshold) {
            return HeatMapColor.CRITICAL_HIGH;
        }
        if (metricMetadata.highValueWarningThreshold !== undefined && value >= metricMetadata.highValueWarningThreshold) {
            return HeatMapColor.WARNING_HIGH;
        }
        return HeatMapColor.NORMAL;
    }

    return HeatMapColor.NO_DATA;
};



/**
 * If resolution is SECONDS (e.g. 1, 5, 10, ...) we will create a N columns for which N * resolution = 60 seconds. So
 * if resolution is 1 sec, we will create 60 columns. If resolution is 5 secs, we will create 12 columns, and
 * so on. Then, we will create M rows, one for each Minute. So the grid will be like this
 *      10:01
 *      10:02
 *      10:03
 *       ...
 *             01        02        03      04       06    ....           // if resolution is 1 sec
 *          00 - 05   05 - 10   10 - 15  15 - 20  20 - 25    ....        // if resolution is 5 sec
 */
const createHeatMapDataForDataResolutionInSeconds = (
    resolutionInMs: number,
    dataPointArray: readonly MultiSeriesDataPoint<number>[],
    metricInfo: NumericMetricInfo,
    metricMetadata: MetricMetadata | undefined,
    language: Language,
    trans: TFunction,
): HeatMapMetricData => {

    const startTime = dataPointArray[0].dateValue;
    const startDate = new Date(startTime);
    const endTime = dataPointArray[dataPointArray.length - 1].dateValue;

    /**
     * Normalize the startDate to the beginning of the Minute. This will be the start date in the Heat Map. The reason we need to do this is
     * because the actual startDate can be in the middle of the min like 01:05:24 but we don't want to use that as the start date and show the
     * first cell (0, 0) like this 01:05:00. It would look better for the first cell to be 01:05:00, and then 01:05:01, ..., 01:05:59.
     *
     * So we need to round down the startDate from something like e.g. Jan 3 04:24:35 to Jan 3 04:24:00
     */
    const normalizedStartTime = (new Date(
        startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), startDate.getHours(), startDate.getMinutes(), 0,
    )).valueOf();

    // Get the interval between the last data point and the normalizedStartTime. We will need this to computer the number of ROWS
    const absoluteIntervalInMs = endTime - normalizedStartTime + Time.MINUTE_IN_MS;
    const secondsPerColumn = Math.ceil(resolutionInMs / Time.SECOND_IN_MS);
    const numberOfColumns = Math.ceil(Time.MINUTE_IN_MS / resolutionInMs);
    const numberOfRows = Math.ceil(absoluteIntervalInMs / Time.MINUTE_IN_MS);


    // Create the arrays for storing heat map data
    const heatMapDataPoints: HeatMapDataPoint[] = [];
    const rowLabels: any[] = [];
    const colLabels: any[] = [];


    // Create formatter to format the row labels
    const fullDateFormatter = new Intl.DateTimeFormat(language.code, fullDateFormat);
    const rowDateFormatter = new Intl.DateTimeFormat(language.code, {
        year  : 'numeric',
        month : 'short',
        day   : '2-digit',
        hour  : '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });


    // Create the labels for the rows
    // row labels will be the timestamp display rounded up to the nearest MINUTE e.g. 'yyyy mm dd hh:mm:00'.
    // We don't need to show Seconds here because it will be shown in the col labels
    for (let row = 0; row < numberOfRows; row += 1) {
        const timestamp = normalizedStartTime + (row * Time.MINUTE_IN_MS);
        rowLabels.push(rowDateFormatter.format(timestamp));
    }

    // Create the labels for the columns
    // col labels will be the seconds value for each column. If secondsPerColumn is 1 then the value will just going to be
    // e.g. '01', '02', '03', etc.... If the secondsPerColumn is greater than 1, we show the range. For example if secondsPerColumn = 5 we
    // will show this '00 - 04', '05 - 09', ..., '55 - 59'
    for (let col = 0; col < numberOfColumns; col += 1) {
        let valueDisplay = '';
        if (secondsPerColumn === 1) {
            valueDisplay = col.toString().padStart(2, '0');
        } else {
            const seconds = col * secondsPerColumn;
            // this will produce label "00 - 05", "05 - 10", etc...
            valueDisplay = `${seconds.toString().padStart(2, '0')} - ${(seconds + secondsPerColumn).toString().padStart(2, '0')}`;
        }

        // add label "secs" to the display e.g. '1 sec', '2 sec', or '1 - 5 secs', '5 - 10 secs'
        colLabels.push(trans('label.value_unit_display', {
            value: valueDisplay,
            unit : trans(TimeUnit.SECOND_ABBREV, { count: secondsPerColumn }),
        }));
    }


    // Create a HeatMapDataPoint object for each data point in dataPointArray and add them to the heatMapDataPoints array
    // And at the same time, remember which rowLabels are used by the data. If there are too many rowLabels, we will filter out some of the
    // unused rowLabels
    const usedRowLabels = {};
    dataPointArray.forEach((dataPoint) => {
        const value = dataPoint.values[metricInfo.name];
        const timestamp = dataPoint.dateValue;
        const elapsedTime = timestamp - normalizedStartTime;
        const tooltip = value
            ? `${fullDateFormatter.format(timestamp)}: ${value}${metricInfo.unit ? ` ${metricInfo.unit}` : ''}`
            : `${fullDateFormatter.format(timestamp)}: N/A`;

        // Find the cell (row, col) the data belonged to and set the data point for that cell
        const row = Math.floor(elapsedTime / Time.MINUTE_IN_MS);
        const col = Math.floor((elapsedTime - (row * Time.MINUTE_IN_MS)) / resolutionInMs);
        const rowLabel = rowLabels[row];

        heatMapDataPoints.push({
            color: getValueColorBasedOnThreshold(metricMetadata, value),
            value,
            row  : rowLabel,
            col  : colLabels[col],
            tooltip,
        });

        // Remember that there is a data point that uses the rowLabel so that we don't filter out this rowLabel later
        usedRowLabels[rowLabel] = true;
    });


    return {
        dataPoints: heatMapDataPoints,
        rowLabels : rowLabels.length > MAX_ROWS     // If there are more rowLabels than MAX_ROWS then start removing unused rows
            ? rowLabels.filter((rowLabel) => {
                return usedRowLabels[rowLabel];
            })
            : rowLabels,
        colLabels,
        metricInfo,
    };
};



/**
 * If resolution is MINUTES (e.g. 1, 5, 10, ...) we will create a N columns for which N * resolution = 60 minutes. So
 * if resolution is 1 min, we will create 60 columns. If resolution is 5 minutes, we will create 12 columns, and
 * so on. Then, we will create M rows, one for each hour. So the grid will be like this
 *      10:00
 *      11:00
 *      12:00
 *       ...
 *             01        02        03      04       06    ....           // if resolution is 1 min
 *          00 - 05   05 - 10   10 - 15  15 - 20  20 - 25    ....        // if resolution is 5 min
 */
const createHeatMapDataForDataResolutionInMinutes = (
    resolutionInMs: number,
    dataPointArray: readonly MultiSeriesDataPoint<number>[],
    metricInfo: NumericMetricInfo,
    metricMetadata: MetricMetadata | undefined,
    language: Language,
    trans: TFunction,
): HeatMapMetricData => {

    const startTime = dataPointArray[0].dateValue;
    const startDate = new Date(startTime);
    const endTime = dataPointArray[dataPointArray.length - 1].dateValue;

    /**
     * Normalize the startDate to the beginning of the hour. This will be the start date in the Heat Map. The reason we need to do this is
     * because the actual startDate can be in the middle of the hour like 05:24 but we don't want to use that as the start date and show the
     * first cell (0, 0) like this 05:24. It would look better for the first cell to be 05:00, and then 05:01, ..., 05:59.
     *
     * So we need to round down the startDate from something like e.g. Jan 3 04:24:35 to Jan 3 04:00:00
     */
    const normalizedStartTime = (new Date(
        startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), startDate.getHours(), 0, 0,
    )).valueOf();

    // Get the interval between the last data point and the normalizedStartTime. We will need this to computer the number of ROWS
    const absoluteIntervalInMs = endTime - normalizedStartTime + Time.HOUR_IN_MS;
    const minutesPerColumn = Math.ceil(resolutionInMs / Time.MINUTE_IN_MS);
    const numberOfColumns = Math.ceil(Time.HOUR_IN_MS / resolutionInMs);
    const numberOfRows = Math.ceil(absoluteIntervalInMs / Time.HOUR_IN_MS);


    // Create the arrays for storing heat map data
    const heatMapDataPoints: HeatMapDataPoint[] = [];
    const rowLabels: any[] = [];
    const colLabels: any[] = [];


    // Create formatter to format the row labels
    const fullDateFormatter = new Intl.DateTimeFormat(language.code, fullDateFormat);
    const rowDateFormatter = new Intl.DateTimeFormat(language.code, {
        year  : 'numeric',
        month : 'short',
        day   : '2-digit',
        hour  : '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });


    // Create the labels for the rows
    // row labels will be the timestamp display rounded up to the nearest HOUR e.g. 'yyyy mm dd hh:00:00'.
    // We don't need to show Minutes here because it will be shown in the col labels
    for (let row = 0; row < numberOfRows; row += 1) {
        const timestamp = normalizedStartTime + (row * Time.HOUR_IN_MS);
        rowLabels.push(rowDateFormatter.format(timestamp));
    }

    // Create the labels for the columns
    // col labels will be the minutes value for each column. If minutesPerColumn is 1 then the value will just going to be
    // e.g. '01', '02', '03', etc.... If the minutesPerColumn is greater than 1, we show the range. For example if minutesPerColumn = 5 we
    // will show this '00 - 04', '05 - 09', ..., '55 - 59'
    for (let col = 0; col < numberOfColumns; col += 1) {
        let valueDisplay = '';
        if (minutesPerColumn === 1) {
            valueDisplay = col.toString().padStart(2, '0');
        } else {
            const minutes = col * minutesPerColumn;
            // this will produce label "00 - 05", "05 - 10", etc...
            valueDisplay = `${minutes.toString().padStart(2, '0')} - ${(minutes + minutesPerColumn).toString().padStart(2, '0')}`;
        }

        // add label "mins" to the display e.g. '1 min', '2 min', or '1 - 5 mins', '5 - 10 mins'
        colLabels.push(trans('label.value_unit_display', {
            value: valueDisplay,
            unit : trans(TimeUnit.MINUTE_ABBREV, { count: minutesPerColumn }),
        }));
    }


    // Create a HeatMapDataPoint object for each data point in dataPointArray and add them to the heatMapDataPoints array
    // And at the same time, remember which rowLabels are used by the data. If there are too many rowLabels, we will filter out some of the
    // unused rowLabels
    const usedRowLabels = {};
    dataPointArray.forEach((dataPoint) => {
        const value = dataPoint.values[metricInfo.name];
        const timestamp = dataPoint.dateValue;
        const elapsedTime = timestamp - normalizedStartTime;
        const tooltip = value
            ? `${fullDateFormatter.format(timestamp)}: ${value}${metricInfo.unit ? ` ${metricInfo.unit}` : ''}`
            : `${fullDateFormatter.format(timestamp)}: N/A`;

        // Find the cell (row, col) the data belonged to and set the data point for that cell
        const row = Math.floor(elapsedTime / Time.HOUR_IN_MS);
        const col = Math.floor((elapsedTime - (row * Time.HOUR_IN_MS)) / resolutionInMs);
        const rowLabel = rowLabels[row];

        heatMapDataPoints.push({
            color: getValueColorBasedOnThreshold(metricMetadata, value),
            value,
            row  : rowLabel,
            col  : colLabels[col],
            tooltip,
        });

        // Remember that there is a data point that uses the rowLabel so that we don't filter out this rowLabel later
        usedRowLabels[rowLabel] = true;
    });


    return {
        dataPoints: heatMapDataPoints,
        rowLabels : rowLabels.length > MAX_ROWS     // If there are more rowLabels than MAX_ROWS then start removing unused rows
            ? rowLabels.filter((rowLabel) => {
                return usedRowLabels[rowLabel];
            })
            : rowLabels,
        colLabels,
        metricInfo,
    };
};


/**
 * If resolution is in HOURS (e.g. 1hour, 2hours, 3hours, etc...) we will create a grid of N columns for which N * resolution = 24 hours.
 * So if resolution is 1 hour, we will create 24 columns. If resolution is 2 hours, we will create 12 columns and so on.
 * Then, we will create M rows, 1 for each day.
 *      Jun 05
 *      Jun 06
 *      Jun 07
 *       ...
 *                   0          1          2   ...                                // if resolution is 1 hour
 *                00 - 03    03 - 06    06 - 09    ...                            // if resolution is 3 hours
 */
const createHeatMapDataForDataResolutionInHours = (
    resolutionInMs: number,
    dataPointArray: readonly MultiSeriesDataPoint<number>[],
    metricInfo: NumericMetricInfo,
    metricMetadata: MetricMetadata | undefined,
    language: Language,
    trans: TFunction,
): HeatMapMetricData => {
    const startTime = dataPointArray[0].dateValue;
    const startDate = new Date(startTime);
    const endTime = dataPointArray[dataPointArray.length - 1].dateValue;

    // Normalize the startDate and round it down to the beginning of the day. So if the startDate is Jan 3 04:24:00, the
    // normalizedStartDate will be Jan 3 00:00:00
    const normalizedStartTime = (new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0)).valueOf();

    // Get the interval between the last data point and the normalizedStartTime. We will need this to computer the number of ROWS
    const absoluteIntervalInMs = endTime - normalizedStartTime + Time.DAY_IN_MS;
    const hoursPerColumn = Math.ceil(resolutionInMs / Time.HOUR_IN_MS);
    const numberOfColumns = Math.ceil(Time.DAY_IN_MS / resolutionInMs);
    const numberOfRows = Math.ceil(absoluteIntervalInMs / Time.DAY_IN_MS);


    // Create the arrays for storing heat map data
    const heatMapDataPoints: HeatMapDataPoint[] = [];
    const rowLabels: any[] = [];
    const colLabels: any[] = [];


    // Create formatter to format the row labels
    const fullDateFormatter = new Intl.DateTimeFormat(language.code, fullDateFormat);
    const rowDateFormatter = new Intl.DateTimeFormat(language.code, {
        year : 'numeric',
        month: 'short',
        day  : '2-digit',
    });


    // Create the labels for the rows
    // row labels will be the timestamp display rounded up to the nearest DAY e.g. 'yyyy mm dd'.
    // We don't need to show time here because it will be shown in the col label
    for (let row = 0; row < numberOfRows; row += 1) {
        const timestamp = normalizedStartTime + (row * Time.DAY_IN_MS);
        rowLabels.push(rowDateFormatter.format(timestamp));
    }

    // Create the labels for the columns
    // col labels will be the hours value for each column. If hoursPerColumn is 1 then the value will just going to be
    // e.g. '00', '01', '02', '03', etc.... If the hoursPerColumn is greater than 1, we show the range. For example if hoursPerColumn = 5 we
    // will show this '00 - 05', '05 - 10', ..., '55 - 60'
    for (let col = 0; col < numberOfColumns; col += 1) {
        let valueDisplay = '';
        if (hoursPerColumn === 1) {
            valueDisplay = `${col.toString().padStart(2, '0')}`;
        } else {
            const hour = col * hoursPerColumn;
            // this will produce label "00 - 05", "05 - 10", etc...
            valueDisplay = `${hour.toString().padStart(2, '0')} - ${(hour + hoursPerColumn).toString().padStart(2, '0')}`;
        }

        // add label "hour" to the display e.g. '1 hour', '2 hour', or '1 - 5 hours', '5 - 10 hours'
        colLabels.push(trans('label.value_unit_display', {
            value: valueDisplay,
            unit : trans(TimeUnit.HOUR_ABBREV, { count: hoursPerColumn }),
        }));
    }


    // Create a HeatMapDataPoint object for each data point in dataPointArray and add them to the heatMapDataPoints array
    // And at the same time, remember which rowLabels are used by the data. If there are too many rowLabels, we will filter out some of the
    // unused rowLabels
    const usedRowLabels = {};
    dataPointArray.forEach((dataPoint) => {
        const value = dataPoint.values[metricInfo.name];
        const timestamp = dataPoint.dateValue;
        const elapsedTime = timestamp - normalizedStartTime;
        const tooltip = value
            ? `${fullDateFormatter.format(timestamp)}: ${value}${metricInfo.unit ? ` ${metricInfo.unit}` : ''}`
            : `${fullDateFormatter.format(timestamp)}: N/A`;

        // Find the cell (row, col) the data belonged to and set the data point for that cell
        const row = Math.floor(elapsedTime / Time.DAY_IN_MS);
        const col = Math.floor((elapsedTime - (row * Time.DAY_IN_MS)) / resolutionInMs);
        const rowLabel = rowLabels[row];

        heatMapDataPoints.push({
            color: getValueColorBasedOnThreshold(metricMetadata, value),
            value,
            row  : rowLabel,
            col  : colLabels[col],
            tooltip,
        });

        // Remember that there is a data point that uses the rowLabel so that we don't filter out this rowLabel later
        usedRowLabels[rowLabel] = true;
    });


    return {
        dataPoints: heatMapDataPoints,
        rowLabels : rowLabels.length > MAX_ROWS     // If there are more rowLabels than MAX_ROWS then start removing unused rows
            ? rowLabels.filter((rowLabel) => {
                return usedRowLabels[rowLabel];
            })
            : rowLabels,
        colLabels,
        metricInfo,
    };
};


/**
 * If intervalType is in DAYS, we will create a grid of N columns for which N * resolution = 31. So if resolution is 1 day,
 * we will create 31 columns. If resolution is 2 days, we will create 16 columns and so on. Then we will create M rows, 1
 * for each month.
 *      Jan
 *      Feb
 *      Mar
 *      ...
 *           01  02  03  04  05 ...     // if resolution is 1 day
 *           01  03  05  07  09 ...     // if resolution is 2 days
 */
const createHeatMapDataForDataResolutionInDays = (
    resolutionInMs: number,
    dataPointArray: readonly MultiSeriesDataPoint<number>[],
    metricInfo: NumericMetricInfo,
    metricMetadata: MetricMetadata | undefined,
    language: Language,
    trans: TFunction,
): HeatMapMetricData => {
    const startTime = dataPointArray[0].dateValue;
    const startDate = new Date(startTime);
    const endTime = dataPointArray[dataPointArray.length - 1].dateValue;

    // Normalize the startDate and round it down to the beginning of the Month. So if the startDate is Jan 3 04:24:00, the
    // normalizedStartDate will be Jan 1 00:00:00
    const normalizedStartTime = (new Date(startDate.getFullYear(), startDate.getMonth(), 1, 0, 0, 0)).valueOf();

    // Get the interval between the last data point and the normalizedStartTime. We will need this to computer the number of ROWS
    const absoluteIntervalInMs = endTime - normalizedStartTime + Time.MONTH_IN_MS;

    const daysPerColumn = Math.ceil(resolutionInMs / Time.DAY_IN_MS);
    const numberOfColumns = Math.ceil((Time.DAY_IN_MS * 31) / resolutionInMs);
    const numberOfRows = Math.ceil(absoluteIntervalInMs / (Time.DAY_IN_MS * 31));


    // Create the arrays for storing heat map data
    const heatMapDataPoints: HeatMapDataPoint[] = [];
    const rowLabels: any[] = [];
    const colLabels: any[] = [];


    // Create formatter to format the row labels
    const fullDateFormatter = new Intl.DateTimeFormat(language.code, fullDateFormat);
    const rowDateFormatter = new Intl.DateTimeFormat(language.code, { month: 'short' });


    // Create the labels for the rows
    // row labels will be the timestamp display rounded up to the nearest MONTH e.g. 'yyyy mm'.
    // We don't need to show day or time here because it will be shown in the col label
    for (let row = 0; row < numberOfRows; row += 1) {
        // Unlike MINUTES or HOURS resolution which each row has fixed number of Minutes or Hours. We need to get the Month for
        // each row but we don't know the number of days, it could be 28, 29, 30, or 31. So we will use 'moment' to handle it.
        const timestamp = moment(normalizedStartTime).add(row, 'month').valueOf();
        rowLabels.push(rowDateFormatter.format(timestamp));
    }

    // Create the labels for the columns
    // col labels will be the hours value for each column. If hoursPerColumn is 1 then the value will just going to be
    // e.g. '01', '02', '03', etc.... If the hoursPerColumn is greater than 1, we show the range. For example if hoursPerColumn = 5 we
    // will show this '01 - 05', '06 - 10', ..., '28 - 29'
    for (let col = 0; col < numberOfColumns; col += 1) {
        let valueDisplay = '';

        // NOTE: col is 0 based but Day display is 1 base so we need to add 1
        if (daysPerColumn === 1) {
            valueDisplay = `${(col + 1).toString().padStart(2, '0')}`;
        } else {
            const day = col * daysPerColumn + 1;
            valueDisplay = `${day.toString().padStart(2, '0')} - ${(day + daysPerColumn - 1).toString().padStart(2, '0')}`;
        }

        // add label "day" to the display e.g. '1 day', '2 day', or '1 - 5 days', '5 - 10 days'
        colLabels.push(trans('label.value_unit_display', {
            value: valueDisplay,
            unit : trans(TimeUnit.DAY_ABBREV, { count: daysPerColumn }),
        }));
    }


    // Create a HeatMapDataPoint object for each data point in dataPointArray and add them to the heatMapDataPoints array
    // And at the same time, remember which rowLabels are used by the data. If there are too many rowLabels, we will filter out some of the
    // unused rowLabels
    const usedRowLabels = {};
    const normalizedStartMonth = moment(normalizedStartTime).toDate().getMonth();
    dataPointArray.forEach((dataPoint) => {
        const value = dataPoint.values[metricInfo.name];
        const timestamp = dataPoint.dateValue;
        const tooltip = value
            ? `${fullDateFormatter.format(timestamp)}: ${value}${metricInfo.unit ? ` ${metricInfo.unit}` : ''}`
            : `${fullDateFormatter.format(timestamp)}: N/A`;

        // Find the cell (row, col) the data belonged to and set the data point for that cell
        const row = moment(timestamp).toDate().getMonth() - normalizedStartMonth;
        const col = moment(timestamp).toDate().getDate() - 1;
        const rowLabel = rowLabels[row];

        heatMapDataPoints.push({
            color: getValueColorBasedOnThreshold(metricMetadata, value),
            value,
            row  : rowLabel,
            col  : colLabels[col],
            tooltip,
        });

        // Remember that there is a data point that uses the rowLabel so that we don't filter out this rowLabel later
        usedRowLabels[rowLabel] = true;
    });


    return {
        dataPoints: heatMapDataPoints,
        rowLabels : rowLabels.length > MAX_ROWS     // If there are more rowLabels than MAX_ROWS then start removing unused rows
            ? rowLabels.filter((rowLabel) => {
                return usedRowLabels[rowLabel];
            })
            : rowLabels,
        colLabels,
        metricInfo,
    };
};



/**
 * Base on the resolution of the data points, create a heat map that can be used to displayed in the Waffle chart
 */
const createHeatMapData = (
    dataPointArray: readonly MultiSeriesDataPoint<number>[],
    metricInfo: NumericMetricInfo,
    metricMetadata: MetricMetadata | undefined,
    language: Language,
    trans: TFunction,
): HeatMapMetricData | undefined => {

    // Only need to create heat map data points if the array has data
    if (dataPointArray.length > 0) {

        // Find the shortest time between any of the 2 data points in the dataPoints array use that as the data's resolution.
        const resolutionInMs = dataPointArray.reduce((min: number, dataPoint: MultiSeriesDataPoint<number>, index: number) => {
            if (index > 0) {
                const diff = dataPoint.dateValue - dataPointArray[index - 1].dateValue;
                if (diff < min) {
                    // the diff between this current data point's date and the previous point's date is smaller than min therefore
                    // return the new min
                    return diff;
                }
            }
            return min;
        }, Number.MAX_SAFE_INTEGER);


        // NOTE: resolutionInMs might not be a full seconds value e.g. 29123. This mean the value is 29s + 123ms. Therefore, we need
        // to round it up to the next seconds, 30000. This formula will do this
        // Math.ceil(29123 / 1000) * 1000 = 30000 = 30 seconds
        const roundedResInMs = Math.ceil(resolutionInMs / Time.SECOND_IN_MS) * Time.SECOND_IN_MS;


        // For now we will NOT support any data resolution that are less than a sec or more than 1 day
        if (roundedResInMs >= Time.SECOND_IN_MS && roundedResInMs <= Time.DAY_IN_MS) {
            if (roundedResInMs < Time.MINUTE_IN_MS) {
                return createHeatMapDataForDataResolutionInSeconds(roundedResInMs, dataPointArray, metricInfo, metricMetadata, language, trans);
            }

            if (roundedResInMs < Time.HOUR_IN_MS) {
                return createHeatMapDataForDataResolutionInMinutes(roundedResInMs, dataPointArray, metricInfo, metricMetadata, language, trans);
            }

            if (roundedResInMs < Time.DAY_IN_MS) {
                return createHeatMapDataForDataResolutionInHours(roundedResInMs, dataPointArray, metricInfo, metricMetadata, language, trans);
            }

            return createHeatMapDataForDataResolutionInDays(roundedResInMs, dataPointArray, metricInfo, metricMetadata, language, trans);
        }
    }

    return undefined;
};



export interface HeatMapDashboardWidgetDataViewContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly selectedWidget: HeatMapDashboardWidgetSettings;

    // This is a container that wraps a DUMB component and some time we need to pass some props such as className, styles, etc... into the
    // component from the container of this container so this is a way for us to pass props into the component
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
    } | undefined;

    readonly widgetPrimaryActions?: readonly BaseCompAction[] | undefined;
    readonly widgetSecondaryActions?: readonly (BaseCompAction | BaseCompSubAction)[] | undefined;
    readonly metricsData?: TSDashboardWidgetRequiredViewData | undefined;
}



/**
 * This is the component for displaying the UI/data view for the HeatMapDashboardWidget.
 */
export const HeatMapDashboardWidgetDataViewContainer: React.FC<HeatMapDashboardWidgetDataViewContainerProps> = (
    props: HeatMapDashboardWidgetDataViewContainerProps,
) => {

    const { state:dataState } = useContext(ritzDataContext);
    const { state:uiState } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const selectedLanguage = getLanguageByCode(SUPPORTED_LANGUAGES, selectLanguage(uiState)) || DEFAULT_LANGUAGE;
    const currentLanguage = loggedInAgent ? getPreferredLanguage(project, loggedInAgent, DEFAULT_LANGUAGE) : selectedLanguage;

    const { trans } = useRitzTranslation(project);
    const classes = useStyle();

    // For heat map widget, we can't display the metricsData as is. We need to convert the metricsData into heat map data format
    // and store the reformatted data in this state. This is the data we will be displaying
    const [allHeatMapMetricsData, setAllHeatMapMetricsData] = useState<(readonly (HeatMapMetricData | undefined)[]) | undefined>();

    // For optimization, use the 'useWidgetDataSource' hook to get the dataSource. This hook will make sure this object
    // doesn't change unless the content of the dataSource changed.
    const dataSource = useWidgetDataSource(props.selectedWidget.dataSource);


    /**
     * Create the export options for the Chart and cache it. We will pass this to the Chart settings later
     */
    const chartExportSettings = useMemo((): ChartSettings['exportSettings'] => {
        return createChartExportSettings(trans);
    }, [trans]);



    useEffect(() => {

        if (props.metricsData && props.metricsData.numericMetrics && props.metricsData.numericMetrics.length > 0
            && props.metricsData.numericMetricsData && props.metricsData.numericMetricsData.length > 0) {

            // For each metric, create the Heat Map data for it
            const heatMapMetricsData = props.metricsData.numericMetrics.map((metric) => {
                return createHeatMapData(
                    props.metricsData?.numericMetricsData || [],
                    metric,
                    dataSource.metricsMetadata[metric.name],
                    currentLanguage,
                    trans,
                );
            });

            // Set the data to the state
            setAllHeatMapMetricsData(heatMapMetricsData);
        } else if (props.metricsData) {
            // If there is metricsData object but it gets here then it means there is no data
            setAllHeatMapMetricsData([]);
        }
    }, [currentLanguage, dataSource.metricsMetadata, props.metricsData, trans]);


    /**
     * Create the view component, Waffle Chart.
     */
    const createViewComponent = useCallback((): React.ReactNode => {

        return (
            <TimeSeriesChartPanel
                className={clsx(classes.widget, props.className, props.infoCompProps?.className, classes.widget)}
                title={props.selectedWidget.title}
                icon={props.selectedWidget.icon}
                customPrimaryActions={props.widgetPrimaryActions}
                customSecondaryActions={props.widgetSecondaryActions}
                showCustomActionOnHover
                seriesCount={allHeatMapMetricsData?.length || 0}
                dataCount={allHeatMapMetricsData?.length || 0}
                noDataMessageComp={(
                    <GenericMessageBox
                        className={classes.noDataMessage}
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                        })}
                    />
                )}
            >
                <div className={clsx(classes.chartsContainer, `layout-${props.selectedWidget.viewSettings.viewLayout}`)}>
                    {allHeatMapMetricsData?.map((heatMapMetricData: HeatMapMetricData | undefined, index) => {
                        if (heatMapMetricData && heatMapMetricData.dataPoints.length > 0) {
                            return (
                                <WaffleChart
                                    key={heatMapMetricData.metricInfo.name}
                                    className={classes.widgetViewContent}
                                    chartId={`widget-${props.selectedWidget.id}-${heatMapMetricData.metricInfo.name}`}
                                    dataPoints={heatMapMetricData.dataPoints || []}
                                    dataFieldSettings={heatMapDataFieldSettingsForToWaffleChart}
                                    seriesName={heatMapMetricData.metricInfo.displayName || heatMapMetricData.metricInfo.name}
                                    colCategories={heatMapMetricData.colLabels}
                                    rowCategories={heatMapMetricData.rowLabels}
                                    showLegends={props.selectedWidget.viewSettings.showLegends}
                                    exportSettings={chartExportSettings}
                                />
                            );
                        }
                        return (
                            <GenericMessageBox
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${heatMapMetricData?.metricInfo.name}-${index}`}
                                title={trans('generic.no_data.title', {
                                    term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                })}
                            />
                        );
                    })}
                </div>
            </TimeSeriesChartPanel>
        );
    }, [allHeatMapMetricsData, chartExportSettings, classes.chartsContainer, classes.noDataMessage, classes.widget,
        classes.widgetViewContent, props.className, props.infoCompProps?.className, props.selectedWidget.icon, props.selectedWidget.id,
        props.selectedWidget.title, props.selectedWidget.viewSettings.showLegends, props.selectedWidget.viewSettings.viewLayout,
        props.widgetPrimaryActions, props.widgetSecondaryActions, trans]);



    return (
        <>
            {allHeatMapMetricsData && createViewComponent()}
        </>
    );
};
